import { DateFormat, formatDate, now } from "common";
import dayjs from "dayjs";
import { produce } from "immer";
import { groupBy, isEqual } from "lodash";
import last from "lodash/last";
import {
    AllowedPrivacy,
    BaseSchedule,
    BaseScheduleRepeat,
    BaseScheduleType,
    Experience,
    Schedule,
    ScheduleDeparture,
    ScheduleOption,
} from "types";
import { getAvailableSchedules } from "./Experience";
import { getAllWeekdays } from "./Weekday";

export const defaultSchedule: Schedule = {
    name: "",
    type: BaseScheduleType.AVAILABLE,
    start: null,
    end: null,
    days: getAllWeekdays(),
    timeRanges: [],
    repeat: BaseScheduleRepeat.WEEKLY,
    departure: ScheduleDeparture.FIXED,
    allowedPrivacies: [],
};

export const getAllowedPrivaciesForAvailableSchedule = (experience: Experience): AllowedPrivacy[] => {
    if (experience.allowedPrivacies?.length === 1) {
        return [...experience.allowedPrivacies];
    }

    const availableSchedules = getAvailableSchedules(experience);
    // If all the schedules have same allowedPrivacies, then there are no schedules added to private pricing
    // so, set the allowedPrivacies same as experience
    const groupedSchedulesByAllowedPrivacies = groupBy(availableSchedules, (schedule) => {
        return schedule.allowedPrivacies?.length;
    });

    if (Object.keys(groupedSchedulesByAllowedPrivacies).length === 1) {
        return experience.allowedPrivacies ? [...experience.allowedPrivacies] : [AllowedPrivacy.PUBLIC];
    } else {
        // This case is for the experiences which have schedules assinged to private pricing. those
        // schedules will have private,public in allowedPrivacies. other schedules will have only public
        // In this case, we need to set the allowedPrivacies to public.
        return [AllowedPrivacy.PUBLIC];
    }
};

export const getDefaultScheduleForExperience = (experience?: Experience, blackoutSchedule = false): Schedule => {
    return produce(defaultSchedule, (draft) => {
        draft.allowedPrivacies = experience?.allowedPrivacies ?? [];
        draft.days = getAllWeekdays();
        draft.type = blackoutSchedule ? BaseScheduleType.UNAVAILABLE : defaultSchedule.type;
    });
};

export const isActive = (schedule: BaseSchedule) => {
    const sellerDate = now().format("YYYY-MM-DD");
    if (schedule.repeat === BaseScheduleRepeat.WEEKLY) {
        if (!!schedule.end && dayjs(schedule.end).diff(dayjs(sellerDate)) < 0) {
            return false;
        }
    } else if (schedule.repeat === BaseScheduleRepeat.CUSTOM) {
        const lastAvailableDate = last(schedule.dates);
        if (lastAvailableDate && sellerDate > lastAvailableDate) {
            return false;
        }
    }

    return true;
};

export const getDefaultBlackoutScheduleForPackage = (): Schedule => {
    return produce(defaultSchedule, (draft) => {
        draft.days = getAllWeekdays();
        draft.type = BaseScheduleType.UNAVAILABLE;
        draft.start = formatDate(now(), DateFormat.DATE_TIME_UTC_ISO);
    });
};

export const getActiveBlackoutSchedules = (schedules: Schedule[]): Schedule[] => {
    return schedules.filter((schedule) => schedule.type === BaseScheduleType.UNAVAILABLE && isActive(schedule));
};

export const haveScheduleOptionsChanged = (before: ScheduleOption[] = [], after: ScheduleOption[] = []): boolean => {
    const toComparable = (options: ScheduleOption[]) =>
        options.map(({ optionId, priority }) => ({ optionId, priority }));
    return !isEqual(toComparable(before), toComparable(after));
};
